import { cloneElement, useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  clearV2Session,
  getV2AccessToken,
  getV2Identity,
  getV2Me,
} from "../auth/v2Auth";
import { canAccessV2 } from "../navigation/v2Access";
import V2LoginPage from "../pages/V2LoginPage";
import V2TeamOverviewPage from "../pages/V2TeamOverviewPage";
import V2Shell from "./V2Shell";
import "../styles/v2.css";

const V2_ROOT =
  "v2-root min-w-[1000px] min-h-screen bg-v2-background text-v2-foreground font-v2 text-[14px] leading-[1.55]";

function V2Guard({ children }) {
  const [state, setState] = useState({ loading: true, me: null, error: null });

  useEffect(() => {
    let active = true;

    if (!getV2AccessToken()) {
      setState({ loading: false, me: null, error: "missing_token" });
      return () => {
        active = false;
      };
    }

    getV2Me()
      .then((me) => {
        if (active) setState({ loading: false, me: me || getV2Identity(), error: null });
      })
      .catch(() => {
        if (active) setState({ loading: false, me: getV2Identity(), error: null });
      });

    return () => {
      active = false;
    };
  }, []);

  if (state.loading) {
    return <V2LoadingState />;
  }

  if (state.error || !getV2AccessToken()) return <Navigate to="/v2/login" replace />;

  const clubId = state.me?.club_id || getV2Identity().club_id;
  if (!canAccessV2(clubId)) return <Navigate to="/" replace />;

  return cloneElement(children, { me: state.me });
}

function V2LoadingState() {
  const { t } = useTranslation();
  return (
    <div className={`${V2_ROOT} px-[clamp(28px,6vw,96px)] py-14`} aria-busy="true">
      <div className="rounded-[10px] border border-v2-border bg-v2-card p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
        {t("v2.common.loading")}
      </div>
    </div>
  );
}

function V2OverviewPage({ me, setToken, setClubId, setIsAuthenticated }) {
  const navigate = useNavigate();
  const handleLogout = () => {
    clearV2Session();
    setToken?.(null);
    setClubId?.(null);
    setIsAuthenticated?.(false);
    navigate("/v2/login", { replace: true });
  };

  return (
    <V2Shell me={me} onLogout={handleLogout}>
      <V2TeamOverviewPage clubId={me?.club_id} />
    </V2Shell>
  );
}

function V2PlaceholderPage({ me, titleKey, descriptionKey, onLogout }) {
  const { t } = useTranslation();
  return (
    <V2Shell me={me} onLogout={onLogout}>
      <div className="mx-auto max-w-[620px] px-8 py-20">
        <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.14em] text-v2-cyan">
          {t("v2.placeholder.overline")}
        </p>
        <h1 className="mb-3 font-display text-[36px] font-bold uppercase leading-none text-v2-foreground">
          {t(titleKey)}
        </h1>
        <p className="mb-6 text-[13px] text-v2-muted">{t(descriptionKey)}</p>
        <button
          type="button"
          className="min-h-[38px] rounded-[5px] border border-v2-brand bg-v2-brand px-3.5 text-[11px] font-semibold text-white"
          onClick={() => window.history.back()}
        >
          {t("v2.placeholder.back")}
        </button>
      </div>
    </V2Shell>
  );
}

export default function V2App({ setToken, setClubId, setIsAuthenticated }) {
  const hasToken = Boolean(getV2AccessToken());

  const handleLogout = () => {
    clearV2Session();
    setToken?.(null);
    setClubId?.(null);
    setIsAuthenticated?.(false);
  };

  return (
    <Routes>
      <Route
        path="login"
        element={
          <V2LoginPage
            setToken={setToken}
            setClubId={setClubId}
            setIsAuthenticated={setIsAuthenticated}
          />
        }
      />
      <Route
        path="overview"
        element={
          <V2Guard>
            <V2OverviewPage
              setToken={setToken}
              setClubId={setClubId}
              setIsAuthenticated={setIsAuthenticated}
            />
          </V2Guard>
        }
      />
      <Route
        path="profile"
        element={
          <V2Guard>
            <V2PlaceholderPage
              titleKey="v2.profile.title"
              descriptionKey="v2.profile.description"
              onLogout={handleLogout}
            />
          </V2Guard>
        }
      />
      <Route
        path="settings"
        element={
          <V2Guard>
            <V2PlaceholderPage
              titleKey="v2.settings.title"
              descriptionKey="v2.settings.description"
              onLogout={handleLogout}
            />
          </V2Guard>
        }
      />
      <Route
        index
        element={<Navigate to={hasToken ? "/v2/overview" : "/v2/login"} replace />}
      />
      <Route
        path="*"
        element={<Navigate to={hasToken ? "/v2/overview" : "/v2/login"} replace />}
      />
    </Routes>
  );
}
